import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  // nextTick,
} from 'vue';
import Fraction from 'fraction.js';
import { Toast } from 'vant';
import { Native } from '@hb/mbridge';
import { debounce } from 'lodash';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import router from '@/router/index.js';
import { ModuleExposeOptions } from '~mall/common/directives/v-expose';
import { showThirdPage } from '~mall/common/utils/jumpUrl';
import AutoCloseTooltip from '~mall/components/AutoCloseTooltip.vue';
import CustomStepper from '~mall/components/CustomStepper.vue';
import RewardCheckbox from '~mall/components/RewardCheckbox/index';
import { useRewardCheckbox } from '~mall/common/utils/useRewardCheckbox';
import store from '~mall/model/store';
import {
  isEasyBikeApp, getAppEnv, isAliMP, isHelloFamilyApp,
} from '~mall/common/env/env';
import {
  categoryMapArr,
  jumpToCategoryLink,
} from '~mall/common/utils/goMonthlyBike';
import { useCountDown } from '~mall/common/utils/useCountDown';
import { useActivityBuy } from '~mall/common/utils/useActivityBuy';
import {
  payWithSignNew,
  checkPayWithSignVersionCompatibility,
} from '~mall/model/apis/pay';
import openLowVersionModal from '~mall/components/LowVersionModal/index';
import { useBikePurchase } from '~mall/pages/mallList/components/bike/useBikePurchase';
import { Package } from '~mall/constants/bike';
import BottomConsentAgreement from '~mall/components/BottomConsentAgreement/index.vue';
import { getStylesBinder } from '~mall/common/utils/styles';
import TicketEmpty from '~mall/components/TicketEmpty';
import NavBar from '~mall/components/NavBar';
import { equityAdapter } from '~mall/model/utils';
import { SkuDetailData } from '~mall/model/store/modules/ticket';
import {
  fetchCombiSellProduct,
  CombiProduct,
  requestSkuDetail,
} from '~mall/model/apis/ticketApis';
import { reqBuyGoods, BuyType } from '~mall/model/apis/order';
import { appMgr } from '~mall/common/appMgr';
import { uiMgr } from '~mall/common/uiMgr';
import { secondToDate } from '~mall/common/utils/time';
import { appQueryConfig } from '~mall/common/utils/url';
import { isRentMini } from '~mall/common/utils/rentUtils';

import NativeMgr from '~mall/common/native/native';
import CardDetail from '../CardDetail';
import styles from './index.module.less';
import RentCouponCard from '../RentCouponCard.vue';
import CombiSellBar from '../CombiSellBar';
import UseNotice from '../UseNotice';

const { handletwBikeBuy } = useBikePurchase();
const { handleActivityBuy: handleActivityBuyHook } = useActivityBuy();

const cx = getStylesBinder(styles);

/** 限时购状态 */
enum LimitStatus {
  /** 无限时抢购 */
  NO = -1,
  /** 已过期 */
  OUTDATED,
  /** 进行中 */
  ONGOING,
  /** 今天开始 */
  TODAY,
  /** 明天开始 */
  TOMORROW,
  /** 明天以后开始 */
  AFTER_TOMORROW,
}

const TimerDescInit = { h: '00', m: '00', s: '00' };

export default defineComponent({
  name: 'TicketDetail',
  setup: () => {
    /** 接口获取的商品详情 */
    const skuData = ref<SkuDetailData | null>(null);
    const timerDesc = ref(TimerDescInit);
    const limitStatus = ref<LimitStatus>(LimitStatus.NO);
    const limitEnd = ref(false);
    const combiProduct = ref<CombiProduct | null>(null);
    const combiProductLoaded = ref(false);
    /** 加价购商品是否选中 */
    const combiSellChecked = ref(false);
    const dataLoaded = ref(false);
    const dataFailLoaded = ref(false);
    const buying = ref(false);
    /** 购买多件步进器  */
    const buyCount = ref(1);
    const isEnableStepper = computed(
      () => Number(router.app.$route.query.quantityStepper) === 1,
    );

    const hideOrder = computed(
      () => Number(router.app.$route.query.hideOrder) === 1,
    );

    const chargeType = computed(
      () => router.app.$route.query.chargeType,
    );

    /** 是否展示签协议信息 */
    const isShowBottomBuyTip = ref(true);
    /** 限时购计时器 */
    let _limitTimer: number | null = null;
    /** 当点击计数器时，展示类似loading */
    const showPromoLoading = ref(false);

    // 活动倒计时相关
    const activityEndTime = ref<number>(0);

    // 使用倒计时 hook
    const {
      formatTimeText: formatActivityTimeText,
      isEnd: activityIsEnd,
      clear: clearActivityCountDown,
      start: startActivityCountDown,
    } = useCountDown({
      endTime: activityEndTime,
      autoStart: false,
      onEnd: () => {
        // 活动结束时的回调
      },
    });

    // 初始化活动倒计时
    const initActivityCountDown = (endTime: number) => {
      activityEndTime.value = endTime;
      startActivityCountDown();
    };

    /** 清理活动计时器 */
    const teardownActivityTimer = () => {
      clearActivityCountDown();
    };

    /** 设置活动计时器 */
    const setupActivityTimer = () => {
      if (!skuData.value?.effectiveActivityInfo) {
        return;
      }

      const { effectiveEndTime } = skuData.value.effectiveActivityInfo;
      initActivityCountDown(effectiveEndTime);
    };

    /** 是否展示加价购——限时购活动不展示 */
    const showCombiSell = computed(
      () => limitStatus.value === LimitStatus.NO,
    );

    /** 是否是商城的两轮卡 */
    const isMallCard = computed(() => skuData?.value?.goodsEquitys
      ?.some((item) => item.propertyType === 2));

    /** 限购是否有库存 */
    const hasLimitStock = computed(() => {
      if (skuData.value) {
        return (skuData.value.activityInfo?.saleStock || 0) > 0;
      }

      return false;
    });

    /** 非限购是否有库存 */
    const hasStock = computed(() => {
      if (skuData.value) {
        return skuData.value.saleNum > 0;
      }

      return false;
    });

    /** 是否有进行中的活动 */
    const hasOnGoingActivity = computed(
      () => limitStatus.value === LimitStatus.ONGOING && hasLimitStock.value,
    );

    /** 是否展示租电动车组件 */
    const showRentCoupons = computed(
      () => isRentMini()
        && skuData.value?.extraInfo
        && String(skuData.value.extraInfo.businessId) === '99',
    );

    /** 奖励金勾选逻辑 */
    const {
      rewardChecked,
      isRewardInPromo,
      isRewardOptional,
      showRewardCheckbox,
      rewardAmount,
      handleRewardChange,
      initRewardChecked,
      getMergedPromotionInfos,
    } = useRewardCheckbox(skuData);

    const countdownEnabled = computed(() => [
      LimitStatus.ONGOING,
      LimitStatus.TODAY,
    ].includes(limitStatus.value));

    /** 是否能够购买 */
    const canBuy = computed(() => {
      if (!skuData.value) {
        return false;
      }

      const { canPurchase, saleNum } = skuData.value;
      return !!canPurchase && saleNum > 0;
    });
    /** 页面标题 */
    const pageTitle = computed(() => skuData.value?.categoryName || '商品详情');
    /** 获取实际售价：优先取服务端返回的优惠价 offerPrice，不存在时走原逻辑兜底 */
    const getRealSalePrice = (data: SkuDetailData) => {
      const {
        salePrice,
        offerPrice,
        // monthlyGoods,
        // promotionInfos,
        // monthlyContractChannel,
      } = data;

      /**
       * 连续包月
       * 历史逻辑售价：是用售价 - 优惠价
       * 20260416迭代优惠决策+奖励金抵现需求中，确定连续包月不在进行优惠决策，不会有优惠价
       * 所以去掉连续包月历史算价逻辑
       */
      // if (monthlyGoods) {
      //   const isAlipayChannel = monthlyContractChannel === 'alipay';
      //   const shouldCalculateMonthlyPrice = isAlipayChannel
      //     ? (isEasyBikeApp || isAliMP)
      //     : isEasyBikeApp;
      //   if (shouldCalculateMonthlyPrice) {
      //     const promoPrice = promotionInfos?.[0]?.worth || 0;
      //     return new Fraction(salePrice || 0).sub(promoPrice).valueOf();
      //   }
      // }

      // 服务端已计算好的优惠价格，优先使用
      if (offerPrice != null) {
        return offerPrice;
      }

      // 以下为 offerPrice 不存在时的兜底逻辑
      if (!hasOnGoingActivity.value) {
        return salePrice;
      }

      return data.activityInfo?.salePrice || salePrice;
    };
    /** 获取价格信息 */
    const getPriceInfo = (
      data: SkuDetailData,
    ): {
      /** 总价 */
      totalPrice: number;
      /** 原总价 */
      totalOriginPrice: number;
      /** 总优惠金额 */
      totalPromoAmount: number;
      /** 实际售价——有限时购活动则展示活动售价 */
      realPrice: number;
      /** 详情页展示价格——与支付按钮一致但不含加价购 */
      displayPrice: number;
    } => {
      const realSalePrice = new Fraction(getRealSalePrice(data)); // 实际售价
      let totalPrice = new Fraction(realSalePrice); // 总价
      let totalOriginPrice = new Fraction(data.originalPrice); // 原总价

      totalPrice = totalPrice.mul(buyCount.value); // 总价乘以购买数量
      totalOriginPrice = totalOriginPrice.mul(buyCount.value); // 原总价乘以购买数量

      // 新奖励金价格处理
      if (isRewardInPromo.value && showRewardCheckbox.value) {
        // 奖励金在 promotionInfos 中：offerPrice 已包含奖励金抵扣
        // 用户取消勾选时，需要把奖励金金额加回去
        if (!rewardChecked.value) {
          totalPrice = totalPrice.add(rewardAmount.value);
        }
      } else if (isRewardOptional.value && showRewardCheckbox.value) {
        // 奖励金在 optionalPromotions 中：offerPrice 不含奖励金
        // 用户勾选时，需要减去奖励金金额（保底为 0，防止服务端数据异常导致负数价格）
        if (rewardChecked.value) {
          totalPrice = new Fraction(Math.max(totalPrice.sub(rewardAmount.value).valueOf(), 0));
        }
      }

      // 记录加价购前的价格，用于详情页展示（不含加价购，但包含奖励金等优惠）
      const displayPrice = new Fraction(totalPrice);

      // 选中加购商品
      if (combiSellChecked.value && combiProduct.value) {
        const { salePrice: combiSalePrice, originalPrice: combiOriginalPrice } = combiProduct.value;
        totalPrice = totalPrice.add(combiSalePrice); // 总价加上加价购售价
        totalOriginPrice = totalOriginPrice.add(combiOriginalPrice); // 原总价加上加价购原价
      }

      return {
        realPrice: realSalePrice.valueOf(), // 实际售价
        displayPrice: displayPrice.valueOf(), // 详情页展示价格（不含加价购）
        totalPrice: totalPrice.valueOf(), // 包含加价购的优惠价格
        totalOriginPrice: totalOriginPrice.valueOf(), // 原总价
        totalPromoAmount: Math.max(
          totalOriginPrice.sub(totalPrice).valueOf(),
          0,
        ), // 总优惠金额
      };
    };

    const dataView = computed(() => {
      const { value } = skuData;

      if (!value) {
        return null;
      }

      const { goodsEquitys } = value;
      return {
        ...value,
        _benefits: (goodsEquitys || []).map((equity) => equityAdapter(equity)),
        _priceInfo: getPriceInfo(value),
      };
    });

    const limitNum = computed(() => {
      const limitNum = Number(dataView.value?.goodsLimit) === 1
        ? dataView.value?.limitNum
        : 50;

      return limitNum;
    });

    // 搭售商品埋点公共参数
    const getCombiUbtBusinessInfo = (combi: CombiProduct | null) => ({
      tyingGoodId: combi?.spuId || '',
      tyingProductBusiness: combi?.categoryId || '',
      tyingProductPrice: combi?.salePrice ?? '',
      tyingProductName: combi?.goodsName || '',
    });


    // 获取搭售商品
    const loadCombiProduct = async (data: SkuDetailData) => {
      combiSellChecked.value = false;
      combiProductLoaded.value = false;

      try {
        if (!showCombiSell.value) {
          return;
        }

        const res = await fetchCombiSellProduct({ spuId: data.spuId });
        combiProduct.value = res.data;

        if (combiProduct.value) {
          // 如果搭售是免费的，则自动选中
          // 修复历史bug，当重新拉这个接口后，搭售商品会自动取消选中
          if (combiProduct.value.salePrice === 0) {
            combiSellChecked.value = true;
          } else {
            combiSellChecked.value = false;
          }

          // 搭售商品曝光埋点
          UBT.moduleExpose({
            pageId: 'platform_couponMerchantdetail',
            categoryId: 'platform',
            moduleId: 'platform_tyingProduct_expose',
            contentId: 'platform_tyingProduct_expose',
            exposureTimes: 1,
            businessInfo: getCombiUbtBusinessInfo(combiProduct.value),
          });
        }
      } finally {
        combiProductLoaded.value = true;
      }
    };

    /** 清理限时购计时器 */
    const teardownLimitTimer = () => {
      if (_limitTimer) {
        window.clearInterval(_limitTimer);
        _limitTimer = null;
      }
    };

    const setupLimitTimer = (useCurrTime: boolean) => {
      if (!skuData.value) {
        return;
      }

      teardownLimitTimer();
      let leftTime = 0;
      const { activityInfo } = skuData.value;

      if (countdownEnabled.value && activityInfo) {
        const now = Date.now();
        const initTime = (useCurrTime && activityInfo.currTime) || now;
        const targetTime = limitStatus.value === LimitStatus.ONGOING
          ? activityInfo.effectiveEndTime
          : activityInfo.effectiveStartTime;
        leftTime = Math.floor(((targetTime || now) - initTime) / 1000);
      }

      _limitTimer = window.setInterval(() => {
        if (leftTime <= 0) {
          teardownLimitTimer();

          if (limitStatus.value === LimitStatus.TODAY) {
            limitStatus.value = LimitStatus.ONGOING;
            setupLimitTimer(false);
          } else {
            limitEnd.value = true;
            limitStatus.value = LimitStatus.NO;
            timerDesc.value = TimerDescInit;
          }
        }
        timerDesc.value = secondToDate(leftTime);
        leftTime -= 1;
      }, 1000);
    };

    /** 初始化限时购 */
    const initLimit = (data: SkuDetailData) => {
      // 接口获取的限时购状态（mall.goods.detail）
      // 接口限时购对象中获取限时购状态
      limitStatus.value = data.activityInfo?.timeLimitFlag || LimitStatus.NO;

      if (countdownEnabled.value) {
        setupLimitTimer(true);
      }
    };

    const updateBottomBuyTip = (isGray: boolean) => {
      isShowBottomBuyTip.value = isGray;
    };

    watch(
      () => skuData.value,
      (value) => {
        teardownLimitTimer();

        if (!value) {
          return;
        }

        initLimit(value);
        loadCombiProduct(value);

        // 启动活动计时器
        setupActivityTimer();

        // 初始化奖励金勾选默认状态
        initRewardChecked();
      },
    );

    watch(pageTitle, (value) => {
      if (!isHelloFamilyApp) {
        Native.setPageTitle({
          title: value,
        });
      }
    });
    const twBike = computed(() => {
      const { twBikeStr = '{}' } = router.app.$route.query;
      return JSON.parse(twBikeStr);
    });
    const categoryName = computed(() => router.app.$route.query.categoryName);
    const isTwBike = computed(() => {
      const { isTwBikeStr } = router.app.$route.query;
      return !!isTwBikeStr;
    });
    const spuIdQuery = computed(() => {
      let { spuId = '' } = router.app.$route.query;
      const questIndex = spuId.indexOf('?');

      if (questIndex !== -1) {
        spuId = spuId.slice(0, questIndex);
      }

      return spuId;
    });


    // 埋点字段：营销标签json字符串
    const marketingLabel = computed(() => skuData.value?.marketingTagName || '');
    // 埋点字段：highRecm
    const highRecm = computed(() => router.app.$route.query?.highRecm || '');

    // 页面埋点公共参数
    const pageUbtBusinessInfo = computed(() => {
      const bizInfo = {
        marketingLabel: marketingLabel.value,
        isSupervalue: highRecm.value,
        adsource: appQueryConfig.adsource,
      };

      if (skuData.value) {
        const {
          spuId, salePrice, originalPrice, activityInfo, categoryId, goodsName,
        } = skuData.value;
        return {
          ...bizInfo,
          spuId,
          // 划线价
          originalPrice: activityInfo?.salePrice ? salePrice : originalPrice,
          /** 券后价 */
          salePrice: activityInfo?.salePrice || salePrice,
          categoryid: categoryId,
          sku_name: goodsName,
        };
      }

      return bizInfo;
    });

    // 购买按钮曝光埋点配置
    const buyBtnExposeOptions = computed<ModuleExposeOptions>(() => ({
      exposeData: {
        pageId: 'platform_couponMerchantdetail',
        categoryId: 'platform',
        moduleId: 'platform_merchantdetail_buy',
        contentId: 'platform_merchantdetail_buy',
        exposureTimes: 1,
        businessInfo: {
          ...getCombiUbtBusinessInfo(combiProduct.value),
          ...pageUbtBusinessInfo.value,
        },
      },
    }));

    /** 获取实际售价 */
    const getRealPrice = (_priceInfo: any) => {
      // 优先展示秒杀价
      if (skuData.value?.effectiveActivityInfo) {
        return skuData.value.effectiveActivityInfo.salePrice;
      }
      return _priceInfo.displayPrice;
    };


    const addPunctuationEnding = (str: string | undefined) => {
      if (!str) return str;
      if (!/[，,。]$/.test(str)) {
        return `${str}，`;
      }
      return str;
    };

    const skuDataAdapter = (twBike: Package) => {
      const desc = twBike.marketingWords
        ?.map(({ wordsDescExt }) => addPunctuationEnding(wordsDescExt))
        .join('') || '';
      const finalDesc = (
        addPunctuationEnding(twBike.commodityDesc) + desc
      ).replace(/[，,。 ]$/, '。');
      return {
        categoryName: categoryName.value,
        ...twBike,
        goodsName: twBike.showName,
        salePrice: Number(twBike.actualPrice.finalPrice),
        originalPrice: twBike.originPrice,
        goodsEquitys: [
          {
            commodityDesc: twBike.commodityDesc,
            effectDays: twBike.cardBatchInfo.effectDay,
            virtualName: twBike.showName,
          },
        ],
        canPurchase: 1,
        saleNum: 1,
        useNotice: `<p>${finalDesc}</p>`,
        picUrl:
          categoryName.value === '单车'
            ? 'https://m.hellobike.com/resource/gallery/290/Eni3Kky5eZ.png?x-oss-process=image/quality,q_80'
            : 'https://m.hellobike.com/resource/gallery/290/pTcYIZ7kGg.png?x-oss-process=image/quality,q_80',
      };
    };
    const loadData = async () => {
      dataLoaded.value = false;
      dataFailLoaded.value = false;
      try {
        // 二轮的详情页面 由于二轮不提供详情的接口 并且不统一 所以这块代码很乱了要
        if (isTwBike.value) {
          // await getCityInfo();
          skuData.value = skuDataAdapter(twBike.value);
          // loadData(props.noCard);
        } else {
          // 非二轮
          skuData.value = await requestSkuDetail({
            spuId: spuIdQuery.value,
            algorithmPriceFlag: router.app.$route.query.algorithmPriceFlag,
          });
        }
        if (!skuData.value) {
          return;
        }
        // 页面进入埋点
        UBT.pageView({
          pageId: 'platform_couponMerchantdetail',
          categoryId: 'platform',
          businessInfo: pageUbtBusinessInfo.value,
        });
      } catch (e) {
        dataFailLoaded.value = true;
      } finally {
        dataLoaded.value = true;
      }
    };

    /** 设置事件监听 */
    const setupHandlers = () => {
      const handler = () => {
        if (document.visibilityState === 'visible') {
          if (!spuIdQuery.value) {
            return;
          }

          loadData();
        }
      };
      document.addEventListener('visibilitychange', handler);

      return () => {
        document.removeEventListener('visibilitychange', handler);
      };
    };
    let teardownHandlers: () => void;

    onMounted(async () => {
      teardownHandlers = setupHandlers();
      await loadData();
      store.commit('skuData/setSkuData', skuData.value);
    });

    // 监听刷新标记，下单后刷新详情数据
    watch(
      () => store.state.reward.detailRefreshFlag,
      () => {
        if (!isTwBike.value && spuIdQuery.value) {
          requestSkuDetail({
            spuId: spuIdQuery.value,
            algorithmPriceFlag: router.app.$route.query.algorithmPriceFlag,
          }).then((res) => {
            if (res) {
              skuData.value = res;
            }
          }).catch(() => { /* 刷新失败不阻塞 */ });
        }
      },
    );

    onBeforeUnmount(() => {
      teardownLimitTimer();
      teardownActivityTimer();
      teardownHandlers();
      // 页面退出埋点
      UBT.pageViewOut({
        pageId: 'platform_couponMerchantdetail',
        categoryId: 'platform',
        businessInfo: pageUbtBusinessInfo.value,
      });
    });

    const displayOriginPriceTip = () => new Promise<void>((resolve) => {
      Toast('当前是非活动时段，下单前请注意购买金额');
      setTimeout(resolve, 1000);
    });

    const placeOrder = async (type: BuyType) => {
      if (!dataView.value) {
        return;
      }
      const mergedPromotionInfos = getMergedPromotionInfos(dataView.value);

      await reqBuyGoods({
        skuData: { ...dataView.value, num: buyCount.value, promotionInfos: mergedPromotionInfos },
        hideOrder: hideOrder.value,
        chargeType: chargeType.value,
        payAmount: dataView.value._priceInfo.totalPrice,
        buyType: type,
        multiNumOrder: !!isEnableStepper.value,
        combiProduct:
          (combiSellChecked.value && combiProduct.value) || undefined,
        highRecm: highRecm.value,
        isFromDetail: true,
      });
    };

    const payWithSignPreCheck = async () => {
      const { version } = await NativeMgr.getDeviceInfo();

      // 使用抽象的版本检查方法
      if (!checkPayWithSignVersionCompatibility(version) && isEasyBikeApp) {
        openLowVersionModal({
          confirm() {
            // UBT.clickButton({
            //   pageId: 'couponmall_list',
            //   buttonName: 'hellomall_appUpgrade',
            //   categoryId: 'hellomall',
            //   businessInfo: {
            //     spuIds: skuData.value?.spuId,
            //     adsource: appQueryConfig.adsource,
            //     marketingLabel: marketingLabel.value,
            //     version,
            //   },
            // });
          },
        });
        return;
      }
      if (!dataView.value) {
        return;
      }
      try {
        // 使用新的 payWithSignNew 方法，传入处理后的 promotionInfos
        const mergedPromotionInfos = getMergedPromotionInfos(dataView.value);
        await payWithSignNew({ ...dataView.value, promotionInfos: mergedPromotionInfos }, true);
      } catch (error) {
        // 支付签约失败
      }
    };

    const handleBuy = async () => {
      if (!dataView.value) {
        return;
      }

      if (buying.value) {
        return;
      }

      // 购买按钮点击埋点
      UBT.clickButton({
        pageId: 'platform_couponMerchantdetail',
        buttonName: 'platform_merchantdetail_buy',
        categoryId: 'platform',
        businessInfo: {
          ...getCombiUbtBusinessInfo(combiProduct.value),
          ...pageUbtBusinessInfo.value,
        },
      });

      // 拼团商品
      if (skuData.value?.collageGoods === 1 && skuData.value?.collageLink) {
        showThirdPage(skuData.value?.collageLink);
        return;
      }

      // 连续包月
      if (skuData.value?.monthlyGoods) {
        // 支付宝渠道支持 App 和支付宝小程序，其他渠道只支持 App
        const isAlipayChannel = skuData.value?.monthlyContractChannel === 'alipay';
        const shouldPreCheck = isAlipayChannel
          ? (isEasyBikeApp || isAliMP)
          : isEasyBikeApp;
        if (shouldPreCheck) {
          payWithSignPreCheck();
          return;
        }
      }

      // 免密签约
      if (isEasyBikeApp && skuData.value?.passwordFreeGoods) {
        payWithSignPreCheck();
        return;
      }

      // app端跳转 (跳转到平台活动落地页中引导用于支付并签约+开通连续包月)， 小程序端正常购买下单
      if (
        isEasyBikeApp
        && skuData.value
        && categoryMapArr[getAppEnv() as 'fat' | 'uat' | 'pro']?.includes(
          skuData.value.categoryId,
        )
      ) {
        jumpToCategoryLink(
          getAppEnv() as 'fat' | 'uat' | 'pro',
          skuData.value.categoryId,
          skuData.value.spuId,
        );
        return;
      }

      if (isTwBike.value) {
        handletwBikeBuy(buying, twBike, router);
        return;
      }

      if (dataView.value.effectiveActivityInfo) {
        handleActivityBuyHook(dataView.value, dataView.value.categoryId);
        return;
      }

      try {
        const buyType = hasOnGoingActivity.value
          ? BuyType.LIMIT
          : BuyType.NORMAL;

        if (buyType === BuyType.NORMAL && !canBuy.value) {
          return;
        }

        if (limitStatus.value > LimitStatus.ONGOING) {
          // 活动还没开始购买商品，给出提示
          await displayOriginPriceTip();
        }

        uiMgr.showLoading();
        await placeOrder(buyType);
      } finally {
        buying.value = false;
      }
    };

    // 创建防抖函数（只初始化一次）
    const updateBuyCountAfterDelay = debounce(async (count: number) => {
      let tempCount = count;
      if (limitNum.value && tempCount > limitNum.value) {
        Toast(`限购${limitNum.value}件`);
        tempCount = limitNum.value;
      }
      // 请求接口获取优惠金额
      try {
        skuData.value = await requestSkuDetail({
          spuId: spuIdQuery.value,
          algorithmPriceFlag: router.app.$route.query.algorithmPriceFlag,
          buyNum: tempCount,
        });
        buyCount.value = tempCount;
        showPromoLoading.value = false;
      } catch (error) {
        // 请求接口失败
      }
    }, 500);

    /** 修改购买数量 */
    const handleBuyCountChange = (val: string) => {
      showPromoLoading.value = true;
      const count = Number(val);
      // 800ms后获取购买数量 及 对应数量的优惠金额
      updateBuyCountAfterDelay(count);
    };

    // 连续包月：支付宝渠道支持 App 和支付宝小程序，其他渠道只支持 App
    // 连续包月：20260416迭代优惠决策+奖励金抵现需求中确定该类型产品不支持优惠决策
    // 所以统一使用totalPrice对价格展示影响不大，同时又统一了价格计算逻辑
    const renderMonthlyPayBtn = () => {
      const { salePrice, _priceInfo } = dataView.value || {};
      const monthlyContractChannel = skuData.value?.monthlyContractChannel;
      const channelText = monthlyContractChannel === 'alipay' ? `开通支付宝连续包月即享，次月起￥${salePrice}元/月续费` : `开通抖音连续包月即享，次月起￥${salePrice}元/月续费`;

      return (
          <div class={cx('buy-btn')} onClick={handleBuy}>
            <div class={cx('buy-btn__text')}>
              <div class={cx('btn__amount')}>
              首月仅需￥{_priceInfo?.totalPrice} 元
              </div>
              <div class={cx('buy-btn__sub')}>
                {channelText}
              </div>
            </div>
          </div>
      );
    };

    // 免密支持优惠决策
    const renderPasswordFreeBtn = () => {
      const { _priceInfo } = dataView.value || {};
      const passwordFreeContractChannel = skuData.value?.passwordFreeContractChannel;
      const channelText = passwordFreeContractChannel === 'alipay' ? '支付宝' : '抖音';
      return (
        <div class={cx('buy-btn')} onClick={handleBuy}>
          <div class={cx('buy-btn__text')}>
            <div class={cx('btn__amount')}>
              补贴后仅需 {_priceInfo?.totalPrice} 元抢
            </div>
            <div class={cx('buy-btn__sub')}>
              开通{channelText}免密支付既享
            </div>
          </div>
        </div>
      );
    };

    // 秒杀不支持优惠决策
    const renderActivityBuyBtn = () => {
      const { salePrice } = skuData.value?.effectiveActivityInfo || {};
      const getActivityTimeText = () => {
        if (activityIsEnd.value) {
          return '活动已结束';
        }
        return `${formatActivityTimeText()}后结束`;
      };

      return (
        <div class={cx('buy-activity-btn')} onClick={handleBuy}>
          <div class={cx('buy-activity-info')}>
            <span class={cx('buy-activity-info-title')}>
              <img src="https://m.hellobike.com/resource/gallery/99/jmE_5r8ih9.png?x-oss-process=image/quality,q_80" />
            </span>
            <span class={cx('buy-activity-info-time')}>
              {getActivityTimeText()}
            </span>
          </div>
          <div class={cx('buy-btn__right')}>
            <span class={cx('buy-btn__right_amount')}>{salePrice}</span>
            <span>元抢购</span>
          </div>
        </div>
      );
    };

    return () => {
      // 是否有限时抢购：有针对限时抢购的逻辑（倒计时处理），没有专门对没有限时做特殊处理！！！
      const hasLimit = limitStatus.value !== LimitStatus.NO;
      return (
        <div class={cx('ticket', { ticket_limit: hasLimit })}>
          <div class={cx('header')}>
            <NavBar title={pageTitle.value} />
          </div>
          {(() => {
            if (!dataLoaded.value) {
              return null;
            }

            if (dataFailLoaded.value) {
              return (
                <TicketEmpty
                  imgAttrs={{
                    src: 'https://m.hellobike.com/resource/helloyun/18121/s7R3W_icon_yichang@2x.png',
                    width: '2.7rem',
                    height: '1.6rem',
                  }}
                  desc="网络不太顺畅哦，请点击重试"
                  btnText="重新加载"
                  btnHandler={loadData}
                />
              );
            }

            if (!dataView.value) {
              return <TicketEmpty />;
            }

            const {
              picUrl,
              goodsName,
              originalPrice,
              activityInfo,
              useNotice,
              marketingWords,
              _benefits,
              _priceInfo,
            } = dataView.value;
            return (
              <div class={cx('main')}>
                <div class={cx('content')}>
                  <div class={cx('banner-wrap')}>
                    <van-image show-loading class={cx('banner')} src={picUrl} />
                  </div>
                  <div class={cx('info-wrap')}>
                    <div class={cx('info')}>
                      <div class={cx('basic')}>
                        <div class={cx('title')}>{goodsName}</div>
                        <div class={cx('price-count-box')}>
                          <div class={cx('price-wrap')}>
                            <span class={cx('price')}>
                              {/* <span class={cx('price__text')}>
                              {(() => {
                                if (promoType.value === promoTypEnum.COUPON) {
                                  return '券后';
                                }
                                return '';
                              })()}
                              </span> */}
                              <span class={cx('price__symbol')}>¥</span>
                              <span class={cx('price__int')}>
                                {getRealPrice(_priceInfo)}
                              </span>
                            </span>
                            {/* {originalPrice > _priceInfo.realPrice && ( */}
                            {_priceInfo.totalOriginPrice
                              > _priceInfo.totalPrice && (
                              <span class={cx('origin-price')}>
                                <span
                                  style={{ textDecoration: 'line-through' }}
                                >
                                  {originalPrice}
                                  元
                                </span>
                              </span>
                            )}
                          </div>
                          {isEnableStepper.value && (
                            <div class={cx('count-wrap')}>
                              <span class={cx('count-limit-tip')}>
                                限购{limitNum.value}件
                              </span>
                              <div
                                class={cx('count-limit-input', {
                                  'max-w': buyCount.value.toString().length > 2,
                                })}
                              >
                                <CustomStepper
                                  max={limitNum.value}
                                  modelValue={buyCount.value}
                                  onChange={handleBuyCountChange}
                                />
                                <AutoCloseTooltip text="支持添加数量" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* 有限时抢购---展示倒计时 */}
                      {hasLimit && (
                        <div class={cx('timer')}>
                          {/* （进行中 /今天开始）+ 倒计时 */}
                          {countdownEnabled.value && timerDesc.value.h && (
                            <div class={cx('timer__info')}>
                              <div class={cx('timer__desc')}>
                                <span class={cx('timer__desc-value')}>
                                  {timerDesc.value.h}
                                </span>
                                <span class={cx('timer__desc-separator')}>
                                  :
                                </span>
                                <span class={cx('timer__desc-value')}>
                                  {timerDesc.value.m}
                                </span>
                                <span class={cx('timer__desc-separator')}>
                                  :
                                </span>
                                <span class={cx('timer__desc-value')}>
                                  {timerDesc.value.s}
                                </span>
                              </div>
                              <span class={cx('timer__text')}>
                                {(() => {
                                  if (
                                    limitStatus.value !== LimitStatus.ONGOING
                                  ) {
                                    return '后开抢';
                                  }
                                  return limitEnd.value ? '已结束' : '后结束';
                                })()}
                              </span>
                            </div>
                          )}
                          {limitStatus.value > LimitStatus.TODAY
                            && activityInfo && (
                              <div class={cx('timer__info')}>
                                <span>{activityInfo.timeLimitDesc}</span>
                              </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div class={cx('package-info')}>
                      <div class={cx('package-title')}>套餐详情</div>
                      <CardDetail cardList={_benefits} />
                    </div>
                  </div>
                  {showRentCoupons.value && <RentCouponCard />}
                  <div class={cx('use-notice')}>
                    <UseNotice
                      title="使用须知"
                      desc={useNotice || ''}
                      twoWheelCardDescList={marketingWords}
                    />
                  </div>
                </div>
                {(() => {
                  const { totalPrice, totalPromoAmount } = _priceInfo;
                  return (
                    <div
                      class={cx(
                        'footer',
                        (showCombiSell.value && combiProduct.value) && ['footer_combi', 'footer_combi_long'],
                      )}
                    >
                      {/* 搭售栏：放在 footer 内部，通过 absolute 定位到 footer 上方 */}
                      {(() => {
                        if (!showCombiSell.value || !combiProduct.value) {
                          return null;
                        }
                        return (
                          <div class={cx('bar-inner')}>
                            <CombiSellBar
                              data={combiProduct.value}
                              value={combiSellChecked.value}
                              {...{
                                on: {
                                  input: (value: boolean) => {
                                    combiSellChecked.value = value;
                                  },
                                },
                              }}
                            />
                          </div>
                        );
                      })()}
                      <div class={cx('footer-content-wrap')} v-node-expose={buyBtnExposeOptions.value}>
                        {/* 奖励金勾选项：位于购买按钮上方 */}
                        {showRewardCheckbox.value && (
                          <RewardCheckbox
                            checked={rewardChecked.value}
                            amount={rewardAmount.value}
                            {...{
                              on: {
                                change: handleRewardChange,
                              },
                            }}
                          />
                        )}
                        {(() => {
                          const { btnType, btnText, showAmount } = (() => {
                            if (limitStatus.value === LimitStatus.ONGOING) {
                              if (hasLimitStock.value) {
                                return {
                                  btnType: 'limit-now-one',
                                  btnText: '抢购',
                                  showAmount: true,
                                };
                              }

                              if (!hasStock.value) {
                                return {
                                  btnType: 'limit-no',
                                  btnText: '已抢完',
                                  showAmount: false,
                                };
                              }
                            }
                            const useOriginPrice = limitStatus.value >= LimitStatus.ONGOING;
                            return {
                              btnType: '',
                              btnText: (() => {
                                if (useOriginPrice) {
                                  return '原价购买';
                                }

                                if (canBuy.value) {
                                  return '购买';
                                }

                                return dataView.value.canPurchase
                                  ? '卖光了'
                                  : '商品已下架';
                              })(),
                              showAmount:
                                !useOriginPrice && canBuy.value,
                            };
                          })();

                          if (dataView.value.effectiveActivityInfo) {
                            return renderActivityBuyBtn();
                          }

                          // 连续包月：支付宝渠道支持 App 和支付宝小程序，其他渠道只支持 App
                          if (skuData.value?.monthlyGoods) {
                            const isAlipayChannel = skuData.value.monthlyContractChannel === 'alipay';
                            const shouldRenderMonthlyBtn = isAlipayChannel
                              ? (isEasyBikeApp || isAliMP)
                              : isEasyBikeApp;
                            if (shouldRenderMonthlyBtn) {
                              return renderMonthlyPayBtn();
                            }
                          }

                          // 免密
                          if (isEasyBikeApp && skuData.value?.passwordFreeGoods) {
                            return renderPasswordFreeBtn();
                          }

                          return (
                            <div>
                              <div
                                class={cx('buy-btn', {
                                  'buy-btn_disabled': !canBuy.value,
                                  [`buy-btn_${btnType}`]: !!btnType,
                                })}
                                onClick={handleBuy}
                              >
                                <div class={cx('buy-btn__text')}>
                                  {isShowBottomBuyTip.value
                                  && isTwBike.value ? (
                                    <div>
                                      <span class={cx('buy-btn__access')}>
                                        同意并支付
                                      </span>
                                      <div class={cx('buy-btn__sub-access')}>
                                        {totalPrice}元{btnText}
                                      </div>
                                    </div>
                                    ) : (
                                    <div>
                                      {showAmount && [
                                        <span class={cx('buy-btn__amount')}>
                                          {showPromoLoading.value
                                            ? '...'
                                            : totalPrice}
                                        </span>,
                                        <span>元</span>,
                                      ]}
                                      <span>{btnText}</span>
                                      {showAmount && !!totalPromoAmount && (
                                        <div class={cx('buy-btn__sub')}>
                                          已优惠{' '}
                                          {showPromoLoading.value
                                            ? '...'
                                            : totalPromoAmount}
                                          元
                                        </div>
                                      )}
                                    </div>
                                    )}
                                </div>
                              </div>
                              {isShowBottomBuyTip.value
                                && (isTwBike.value || isMallCard.value) && (
                                  <BottomConsentAgreement
                                    show={isShowBottomBuyTip.value}
                                    categoryName={categoryName.value}
                                    handleUpdateBottomTip={updateBottomBuyTip}
                                  />
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>
      );
    };
  },
});
